import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  RoutingService,
  AuthRedirectStorageService,
  AuthFlowRoutesService,
} from "@spartacus/core";
import { take } from "rxjs/operators";
import { KnBrCommonService } from "./kn-br-common.service";

/**
 * Responsible for saving last accessed page (or attempted) before login and for redirecting to that page after login.
 */
@Injectable({
  providedIn: "root",
})
export class KnBrAuthRedirectService {
  /**
   * This service is responsible for redirecting to the last page before authorization. "The last page" can be:
   * 1. Just the previously opened page; or
   * 2. The page that we just tried to open, but AuthGuard cancelled it
   *
   * For example:
   * 1. The user opens the product page, then clicks /login link and signs in
   *    -> Then we should redirect to the product page; or
   * 2. The user opens the product page, then he clicks /my-account link,
   *    but is automatically redirected to the login page by the AuthGuard, and he signs in
   *    -> Then we should redirect to the my-account page, not the product page
   */
  constructor(
    protected routing: RoutingService,
    protected router: Router,
    protected authRedirectStorageService: AuthRedirectStorageService,
    protected knBrCommonService: KnBrCommonService,
    protected authFlowRoutesService: AuthFlowRoutesService
  ) {}

  private ignoredUrls = new Set<string>();
  private lastAuthGuardNavigation: {
    url: string;
    navigationId: number;
  };

  /**
   * Redirect to saved url (homepage if nothing is saved).
   * update 2025 for cwt customer exclusively we only need to go to home page in every scenario
   * hence modified the code accordingly
   */
  redirect() {
    this.knBrCommonService.getRedirectionDetail().subscribe((login: any) => {
      // this.knBrCommonService.redirectionDetail$.next(login);
      if (login && login.redirectURL) {
        this.routing.go("/", { state: { popupMessage: "Show popup" } });
        // this.routing.goByUrl(login.redirectURL);
      } else {
        this.routing.go("/", { state: { popupMessage: "Show popup" } });
        // this.routing.go('/');
      }
      this.authRedirectStorageService.setRedirectUrl(undefined);

      this.lastAuthGuardNavigation = undefined;
    });
  }

  /**
   * Saves url of a page that user wanted to access, but wasn't yet logged in.
   */
  reportAuthGuard() {
    const { url, navigationId } = this.getCurrentNavigation();
    this.lastAuthGuardNavigation = { url, navigationId };
    this.authRedirectStorageService.setRedirectUrl(url);
  }

  /**
   * Saves url of a page that was accessed before entering a page only for not auth users.
   */
  reportNotAuthGuard() {
    const { url, initialUrl, navigationId } = this.getCurrentNavigation();

    this.ignoredUrls.add(url);

    // Don't save redirect url if you've already come from page with NotAuthGuard (i.e. user has come from login to register)
    if (!this.ignoredUrls.has(initialUrl)) {
      // We compare the navigation id to find out if the url cancelled by AuthGuard (i.e. my-account) is more recent
      // than the last opened page
      if (
        !this.lastAuthGuardNavigation ||
        this.lastAuthGuardNavigation.navigationId < navigationId - 1
      ) {
        this.authRedirectStorageService.setRedirectUrl(initialUrl);

        this.lastAuthGuardNavigation = undefined;
      }
    }
  }

  /**
   * Saves the url of the current navigation as the redirect url, unless
   * the url is a part of the user login flow.
   */
  saveCurrentNavigationUrl(): void {
    const navigation = this.router.getCurrentNavigation();
    if (!navigation?.finalUrl) {
      return;
    }

    const url = this.router.serializeUrl(navigation.finalUrl);
    this.setRedirectUrl(url);
  }

  /**
   * Save the url as the redirect url, unless it's a part of the user login flow.
   */
  setRedirectUrl(url: string): void {
    if (!this.authFlowRoutesService.isAuthFlow(url)) {
      this.authRedirectStorageService.setRedirectUrl(url);
    }
  }

  private getCurrentNavigation(): {
    navigationId: number;
    url: string;
    initialUrl: string;
  } {
    const initialUrl = this.router.url;
    const navigation = this.router.getCurrentNavigation();
    const url = this.router.serializeUrl(navigation.finalUrl);
    return {
      navigationId: navigation.id,
      url,
      initialUrl,
    };
  }
}
